import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useAuthContext } from 'src/auth/hooks';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { client, joinErrors } from 'src/api/client';
import { generateDateOptions } from './selected-food-table';

// ----------------------------------------------------------------------

export default function SelectedFoodPrintExcelPage({ restaurantId }: { restaurantId?: string }) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  // Generate date options
  const dateOptions = React.useMemo(() => generateDateOptions(), []);

  // Loading state for download operations
  const isLoading = useBoolean(false);

  // Date selection
  // Default to the last date in the list (latest available date)
  const [selectedDate, setSelectedDate] = useState<string>(
    dateOptions.length > 0 ? dateOptions[dateOptions.length - 1].value : ''
  );

  // Handle date change
  const handleDateChange = (event: SelectChangeEvent<string>) => {
    setSelectedDate(event.target.value);
  };

  // Handle Excel download
  const handleDownloadExcel = async () => {
    // Validate date
    if (!selectedDate) {
      enqueueSnackbar('Please select a date first', { variant: 'warning' });
      return;
    }

    try {
      isLoading.onTrue();

      // Make the request using client.GET
      const { data, error } = await client.GET('/partners/selectedfoods-customers-xlsx/', {
        params: {
          query: {
            date: selectedDate,
            ...(restaurantId && { restaurant_id: restaurantId }),
          }
        },
        parseAs: 'blob'
      });

      if (error) {
        throw new Error('Error fetching Excel file');
      }

      // Create filename with date and timestamp
      const now = new Date();
      const dateFormatted = format(new Date(selectedDate), 'dd-MM-yyyy');
      const timeFormatted = format(now, 'HH_mm_ss');
      const filename = `${dateFormatted}_${timeFormatted}_selected_food.xlsx`;

      // Create download link with the blob data
      const blob = data as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      enqueueSnackbar('Excel file downloaded successfully', { variant: 'success' });

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      enqueueSnackbar(`Error downloading Excel file: ${joinErrors(error)}`, { variant: 'error' });
    } finally {
      isLoading.onFalse();
    }
  };

  return (
    <PermissionBasedGuard hasContent permissions={['partners:read:selectedfood']}>
      <Container maxWidth={false}>
      <CustomBreadcrumbs
          heading="Print/Excel"
          sx={{ mb: 2 }}
          links={[{}]}
        />

        {/* Date Selection and Download */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Select Date
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth required>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  value={selectedDate}
                  onChange={handleDateChange}
                  label="Date *"
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 400 },
                    },
                  }}
                >
                  {dateOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={option.isToday ? {
                        fontWeight: 'bold',
                        color: 'primary.main',
                        backgroundColor: 'rgba(0, 171, 85, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 171, 85, 0.15)',
                        }
                      } : {}}
                    >
                      {option.isToday ? (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Iconify icon="mdi:calendar-today" sx={{ mr: 1, color: 'primary.main' }} />
                          {option.label}
                        </Box>
                      ) : (
                        option.label
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!selectedDate && (
            <Typography color="error" sx={{ mt: 2 }}>
              Please select a date to download Excel file
            </Typography>
          )}

          {/* Download Button */}
          <Box sx={{ display: 'flex', justifyContent: 'right', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleDownloadExcel}
              disabled={!selectedDate || isLoading.value}
              startIcon={
                isLoading.value ? (
                  <CircularProgress color="inherit" size={20} thickness={2} />
                ) : (
                  <Iconify icon="mdi:file-excel" />
                )
              }
              sx={{
                minWidth: 200,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              {isLoading.value ? 'Downloading...' : 'Download Excel'}
            </Button>
          </Box>
        </Card>
      </Container>
    </PermissionBasedGuard>
  );
}